import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavigation from '../../../shared/components/layout/PublicNavigation';
import Footer from '../../../shared/components/layout/Footer';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNavigation />
      <div className="flex-grow py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-white shadow-sm rounded-lg p-8 sm:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Last Updated: January 14, 2026</p>

          <div className="prose prose-gray max-w-none space-y-8">
            <section>
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy explains how Vester LLC ("Vester," "we," "us," or "our") collects, uses, discloses, 
                and protects information when you access or use the Vester website, mobile or web applications, and related 
                services (collectively, the "Service").
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                By using the Service, you agree to this Privacy Policy. If you do not agree, do not use the Service.
              </p>
            </section>

            <section className="bg-blue-50 border-l-4 border-blue-400 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Summary of Key Points</h2>
              <ul className="space-y-3 text-gray-700">
                <li>
                  <strong>What we collect:</strong> Account info (like email), user-entered investment data (including 
                  dollar values), friend/tier settings, and chat messages. We also collect basic device/log data.
                </li>
                <li>
                  <strong>How we use it:</strong> To operate the Service (portfolio features, friends views, chat), 
                  improve/debug (especially during beta), keep the Service safe, and comply with law.
                </li>
                <li>
                  <strong>Who can see it:</strong> Depending on your settings, your friends may see limited portfolio 
                  metrics. Vester administrators may be able to access data (including chat and portfolio entries) to 
                  operate and secure the Service.
                </li>
                <li>
                  <strong>Third parties:</strong> We use third-party providers like Supabase to host/authenticate/store 
                  data on our behalf.
                </li>
                <li>
                  <strong>Selling data:</strong> We do not sell personal information today. If that changes, we will 
                  update this policy and provide notice as required by law.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Scope and Relationship to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy is part of, and incorporated into, the Vester{' '}
                <Link to="/terms" className="text-green-600 hover:text-green-500 underline">
                  Terms and Conditions
                </Link>
                {' '}(the "Terms"). Capitalized terms not defined here have the meanings in the Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">A) Information you provide to us</h3>
              <p className="text-gray-700 leading-relaxed mb-4">You may provide:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Account and profile information:</strong> email address, username, profile details you choose to add
                </li>
                <li>
                  <strong>User-entered investment information:</strong> tickers, transactions, prices, quantities, dates, 
                  notes, and other portfolio-related inputs
                  <div className="mt-2 ml-4 text-sm italic text-gray-600">
                    Important: Much of this content is user-entered and may be incomplete, inaccurate, or even fabricated.
                  </div>
                </li>
                <li>
                  <strong>Social features data:</strong> friends list, friend request activity, and privacy-tier settings 
                  that control what others can see
                </li>
                <li>
                  <strong>Chat content:</strong> messages and other content you send through the Service
                </li>
                <li>
                  <strong>Support communications:</strong> information you provide when you contact us (e.g., email)
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">B) Information collected automatically</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you use the Service, we (and our service providers) may collect:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Log and usage data:</strong> timestamps, pages/screens viewed, actions taken, performance logs, 
                  error logs
                </li>
                <li>
                  <strong>Device and connection data:</strong> IP address, browser type, device type, operating system, 
                  and similar technical identifiers
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">C) Information we do not intend to collect</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Passwords in plaintext:</strong> Authentication is handled via our providers (e.g., Supabase Auth). 
                  We do not ask you to share your password with us via chat or support.
                </li>
                <li>
                  <strong>Brokerage credentials:</strong> At this time, we do not collect brokerage usernames/passwords.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">We may use information to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide and operate the Service (accounts, portfolios, friend-tier views, chat)</li>
                <li>Personalize your experience (settings, preferences, friend-tier controls)</li>
                <li>
                  Maintain safety and integrity (fraud prevention, abuse prevention, enforcing our Terms, investigating 
                  suspected violations)
                </li>
                <li>
                  Debug, improve, and develop features (especially during beta, including troubleshooting performance 
                  issues and fixing bugs)
                </li>
                <li>Communicate with you (service notices, support responses, policy updates)</li>
                <li>Comply with legal obligations and respond to lawful requests</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. How Social Sharing Works (Friends + Tiers)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Service includes features that let you share certain information with friends depending on the 
                friendship tier and your settings.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                The specific categories of information shared with others depend on your configuration and the tier you 
                grant a friend.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You understand that what others see may be derived from user-entered data and may not reflect actual 
                brokerage records or real-world holdings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Admin Access and Internal Visibility</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To operate, secure, support, and improve the Service, Vester administrators and authorized personnel may 
                be able to access information stored in the Service, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>user-entered portfolio values and transactions,</li>
                <li>chat messages and related metadata,</li>
                <li>account and technical logs.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We do not commit to "never looking" at user data. However, we intend to limit access to what is reasonably 
                necessary for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
                <li>support requests,</li>
                <li>debugging and feature development,</li>
                <li>safety/security investigations,</li>
                <li>compliance with law.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. How We Share Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may share information in the following situations:
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">A) Service providers (processors)</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use third-party vendors to help run the Service (for example, hosting, authentication, databases, 
                monitoring). These providers process information on our behalf under contractual obligations.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Supabase:</strong> We use Supabase for authentication and data storage/hosting. Supabase's policies 
                govern how Supabase handles data as a service provider. We encourage you to review{' '}
                <a
                  href="https://supabase.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-500 underline"
                >
                  Supabase's privacy policy
                </a>
                .
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">B) Legal and safety reasons</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may disclose information if we believe doing so is necessary to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>comply with law, regulation, subpoena, court order, or other legal process,</li>
                <li>protect the rights, property, and safety of Vester, our users, or others,</li>
                <li>
                  investigate and enforce our Terms, including suspected fraud, threats, harassment, market manipulation, 
                  or other unlawful conduct.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">C) Business transfers</h3>
              <p className="text-gray-700 leading-relaxed">
                If we are involved in a merger, acquisition, financing, reorganization, bankruptcy, or sale of assets, 
                user information may be transferred as part of that transaction.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">D) With your direction</h3>
              <p className="text-gray-700 leading-relaxed">
                We may share information when you instruct us to (for example, sharing a profile element publicly if you 
                add a public profile feature later).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Accuracy, User Content, and User Responsibility</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Service relies heavily on user-generated content, including manually entered portfolio data and chat 
                messages.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not verify that user-entered transactions or portfolio values match brokerage records.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You are responsible for the content you submit and for how you use information you see in the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Retention and Deletion</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Because the Service is in beta, our retention practices may evolve.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We generally keep information as long as reasonably necessary to operate the Service, comply with law, 
                resolve disputes, enforce our agreements, and for legitimate business purposes.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you request deletion, we will make reasonable efforts to delete or anonymize information, subject to 
                legal requirements and practical limitations (for example, security logs, fraud prevention, and technical 
                constraints).
              </p>
              <p className="text-gray-700 leading-relaxed">
                We do not promise a fixed deletion timeline.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We use reasonable administrative, technical, and organizational safeguards designed to protect information. 
                However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Cookies and Tracking</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may use cookies or similar technologies to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>keep you signed in,</li>
                <li>remember preferences,</li>
                <li>understand usage patterns,</li>
                <li>improve performance and reliability.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                If we introduce third-party analytics or advertising tools later, we will update this Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                The Service is intended for users 18 years and older. We do not knowingly collect personal information 
                from children. If you believe a minor has provided us information, contact us and we'll take appropriate steps.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Your Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Depending on where you live, you may have rights to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>request access to certain information we hold about you,</li>
                <li>request correction of inaccurate data,</li>
                <li>request deletion (subject to exceptions),</li>
                <li>object to or limit certain processing where applicable.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise rights, contact us using the details below. We may need to verify your identity.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. International Users</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be processed in the United States or other locations depending on where our providers 
                operate. By using the Service, you understand your information may be transferred and processed outside your 
                state or country of residence.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Future Features (Plaid Sync, Learn Content, Data Sales)</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Plaid / brokerage sync</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not currently offer brokerage syncing. If we add Plaid or similar providers in the future, we will:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>update this Privacy Policy to explain what data is accessed, collected, and stored,</li>
                <li>disclose how credentials are handled (typically tokenized through the provider),</li>
                <li>describe sharing and security practices related to the integration.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">"Learn" section</h3>
              <p className="text-gray-700 leading-relaxed">
                If we add educational content, we may collect usage data to improve content quality (e.g., what lessons 
                are viewed), and we will reflect any material changes here.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Selling or sharing data</h3>
              <p className="text-gray-700 leading-relaxed">
                We do not sell personal information today. If we ever decide to sell or share user data (including 
                de-identified or aggregated datasets), we will update this Policy and provide any notices/choices required 
                by applicable law before doing so.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. The "Last Updated" date will reflect changes. If 
                changes are material, we may provide additional notice in the Service or via email.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Questions or requests about privacy can be sent to:
              </p>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700">
                  <strong>Vester LLC</strong><br />
                  Email: <a href="mailto:privacy@vesterapp.com" className="text-green-600 hover:text-green-500 underline">privacy@vesterapp.com</a>
                </p>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              By using Vester, you acknowledge that you have read, understood, and agree to this Privacy Policy.
            </p>
            <p className="text-sm text-gray-500 text-center mt-2">
              For more information, please review our{' '}
              <Link to="/terms" className="text-green-600 hover:text-green-500 underline">
                Terms and Conditions
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
